import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  FileText,
  CheckCircle,
  User,
  Calendar,
  Image,
  Video,
  File,
  Clock,
  Loader2,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { HtmlRenderer } from '../../components/editor';
import { BUNDLE_STATUSES, WORK_ITEM_STATUSES } from '@yoda/shared';
import type { BundleStatus } from '@yoda/shared';

interface WorkItemSummary {
  id: string;
  title: string;
  type: string;
  status: string;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  createdAt: string;
}

interface BundleDetail {
  id: string;
  projectId: string;
  requestId: string | null;
  title: string;
  descriptionHtml: string | null;
  status: BundleStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  project: { id: string; name: string };
  request: { id: string; title: string } | null;
  creator: { id: string; name: string; avatarUrl: string | null };
  approver: { id: string; name: string; avatarUrl: string | null } | null;
  workItems: WorkItemSummary[];
  workItemCount: number;
  approvedItemCount: number;
}

const typeIcons: Record<string, typeof Image> = {
  image: Image,
  video: Video,
  document: FileText,
  other: File,
};

export function BundleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bundle, setBundle] = useState<BundleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBundle();
    }
  }, [id]);

  const fetchBundle = async () => {
    setLoading(true);
    try {
      const response = await api.get<BundleDetail>(`/bundles/${id}`);
      setBundle(response.data || null);
    } catch (error) {
      console.error('Failed to fetch bundle:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!bundle || !window.confirm('Approve this bundle and all its work items?')) return;
    setApproving(true);
    try {
      await api.post(`/bundles/${bundle.id}/approve`);
      fetchBundle();
    } catch (error) {
      console.error('Failed to approve bundle:', error);
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="text-center py-12">
        <p className="text-text-secondary">Bundle not found</p>
        <Link to="/bundles" className="text-accent hover:underline mt-2 inline-block">
          Back to Bundles
        </Link>
      </div>
    );
  }

  const statusInfo = BUNDLE_STATUSES[bundle.status];
  const progress = bundle.workItemCount > 0
    ? Math.round((bundle.approvedItemCount / bundle.workItemCount) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl hover:bg-bg-secondary transition-colors mt-1"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-accent/10">
              <Package className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{bundle.title}</h1>
              <div className="flex items-center gap-2 text-text-secondary">
                <Link to={`/projects/${bundle.projectId}`} className="hover:text-accent">
                  {bundle.project.name}
                </Link>
                <span>-</span>
                <span>{bundle.workItemCount} items</span>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-3 mt-3">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{
                backgroundColor: statusInfo?.bgColor,
                color: statusInfo?.color,
              }}
            >
              {statusInfo?.label || bundle.status}
            </span>

            {bundle.status === 'ready_for_review' && (
              <button
                onClick={handleApprove}
                disabled={approving}
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {approving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve Bundle
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-bg-secondary rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Progress</h2>
          <span className="text-sm text-text-secondary">
            {bundle.approvedItemCount} / {bundle.workItemCount} approved
          </span>
        </div>
        <div className="h-3 bg-bg-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Description */}
      {bundle.descriptionHtml && (
        <div className="bg-bg-secondary rounded-2xl p-6">
          <h2 className="text-lg font-semibold mb-4">Description</h2>
          <HtmlRenderer html={bundle.descriptionHtml} />
        </div>
      )}

      {/* Work Items */}
      <div className="bg-bg-secondary rounded-2xl p-6">
        <h2 className="text-lg font-semibold mb-4">Work Items</h2>

        {bundle.workItems.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No work items yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bundle.workItems.map((item) => {
              const TypeIcon = typeIcons[item.type] || File;
              const itemStatus = WORK_ITEM_STATUSES[item.status as keyof typeof WORK_ITEM_STATUSES];

              return (
                <Link
                  key={item.id}
                  to={`/work-items/${item.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-bg-tertiary transition-colors group"
                >
                  {/* Thumbnail or Icon */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-bg-tertiary flex items-center justify-center">
                    {item.thumbnailUrl ? (
                      <img
                        src={item.thumbnailUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <TypeIcon className="w-5 h-5 text-text-muted" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium hover:text-accent truncate">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <span>{item.type}</span>
                      {!item.fileUrl && (
                        <>
                          <span>-</span>
                          <span className="text-amber-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Awaiting upload
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <span
                    className="px-2 py-1 text-xs font-medium rounded-lg flex-shrink-0"
                    style={{
                      backgroundColor: itemStatus?.bgColor,
                      color: itemStatus?.color,
                    }}
                  >
                    {itemStatus?.label || item.status}
                  </span>

                  {/* Arrow */}
                  <ExternalLink className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Source Request */}
      {bundle.request && (
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-2xl p-6">
          <h2 className="text-sm font-medium text-violet-400 mb-2">Source Request</h2>
          <Link
            to={`/requests/${bundle.request.id}`}
            className="text-accent hover:underline font-medium"
          >
            {bundle.request.title}
          </Link>
        </div>
      )}

      {/* Info */}
      <div className="bg-bg-secondary rounded-2xl p-6 space-y-4">
        <div>
          <h3 className="text-sm font-medium text-text-muted mb-2">Created by</h3>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
              {bundle.creator.avatarUrl ? (
                <img
                  src={bundle.creator.avatarUrl}
                  alt=""
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <User className="w-4 h-4 text-accent" />
              )}
            </div>
            <span className="font-medium">{bundle.creator.name}</span>
          </div>
        </div>

        {bundle.approver && (
          <div>
            <h3 className="text-sm font-medium text-text-muted mb-2">Approved by</h3>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                {bundle.approver.avatarUrl ? (
                  <img
                    src={bundle.approver.avatarUrl}
                    alt=""
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <User className="w-4 h-4 text-accent" />
                )}
              </div>
              <span className="font-medium">{bundle.approver.name}</span>
            </div>
            {bundle.approvedAt && (
              <p className="text-xs text-text-muted mt-1">
                {formatDate(bundle.approvedAt)}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-text-muted text-sm pt-2 border-t border-border">
          <Calendar className="w-4 h-4" />
          <span>Created {formatDate(bundle.createdAt)}</span>
        </div>
      </div>
    </motion.div>
  );
}
